import { getFcmToken, getIsLogged } from '@features/auth';
import { readNativeFcmToken } from '@hooks';
import { useRegisterFcmTokenMutation } from '@services';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Transmet le token FCM au serveur des qu'il est connu, sans attendre un login.
 *
 * Le token etait jusqu'ici envoye uniquement dans le payload de connexion. Or il
 * n'est pas disponible a cet instant sur une installation neuve : le shell natif
 * doit obtenir l'autorisation de notification, s'enregistrer aupres d'APNs, puis
 * attendre que Firebase emette le token — l'utilisateur se connecte generalement
 * avant. La colonne restait alors vide jusqu'a la connexion suivante, et
 * l'appareil ne recevait aucune notification entre-temps.
 *
 * Meme probleme lorsque le token tourne pendant que l'utilisateur est connecte
 * (reinstallation, changement de projet Firebase, rotation) : rien ne le
 * remontait.
 *
 * Ce hook vit dans `core/` et non dans `hooks/` a dessein : `@services` importe
 * deja `@hooks`, l'inverse fermerait un cycle d'imports.
 */
export const useSyncFcmToken = () => {
    const fcmTokenFromStore = useSelector(getFcmToken);
    // Meme raison que dans useLoginPageVM : le store peut etre vide alors que le
    // pont natif detient la valeur.
    const fcmToken = fcmTokenFromStore || readNativeFcmToken();
    const isLogged = useSelector(getIsLogged);
    const [registerFcmToken] = useRegisterFcmTokenMutation();

    // Evite de renvoyer le meme token a chaque rendu.
    const lastSentRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isLogged || !fcmToken || lastSentRef.current === fcmToken) {
            return;
        }

        lastSentRef.current = fcmToken;

        registerFcmToken({ fcm_token: fcmToken })
            .unwrap()
            .catch(() => {
                // Un echec ne doit rien interrompre : on autorise une nouvelle
                // tentative au prochain changement de token.
                lastSentRef.current = null;
            });
    }, [fcmToken, isLogged, registerFcmToken]);

    // Une deconnexion doit permettre un nouvel envoi a la reconnexion.
    useEffect(() => {
        if (!isLogged) {
            lastSentRef.current = null;
        }
    }, [isLogged]);
};
